import { Dispatch, SetStateAction, useState } from 'react';
import {
  MIXPANEL_ENABLE,
  EVENT_NAMES,
  TrackPackagesPageVisitedProps,
  TrackProfileViewProps,
  TRACK_PAGE_VIEW_PATHS,
  MIXPANEL_DEFAULT_STATE,
  MixpanelDefaultStateProps,
  MIXPANEL_CREDITS_PURCHASED,
} from '../constants/mixpanel';
import * as Sentry from '@sentry/react';
import { useMixpanelState } from '../contexts/MixpanelContext';
import { useApp } from '../contexts/AppContext';
import * as sdk from '../sdk';
import { getLocalItem, removeLocalItem } from '../common';
import { mixpanelTrackEvent, mixpanelTrackEventAnonymously } from '../utils/mixpanel/common';

interface UseMixPanelProps {
  trackCreditsPurchased: (payload: any) => void;
  trackPackagesPageVisited: (payload: TrackPackagesPageVisitedProps) => void;
  trackProfileView: (payload: TrackProfileViewProps, isStarted?: boolean) => void;
  trackPageView: (pathname: string) => void;
  trackPageViewAnonymously: (pathname: string) => void;
  mixpanelData: MixpanelDefaultStateProps;
  setMixpanelDataByKeyHandler: Dispatch<SetStateAction<any>>;
  resetMixpanelData: (key: string) => void;
}

const useMixPanel = (): UseMixPanelProps => {
  const { mixpanelData, setMixpanelDataByKeyHandler } = useMixpanelState();
  const { accountHashId, isAccountIsMixpanelEligableEnabled } = useApp();
  const [currentRoute, setCurrentRoute] = useState(null);

  const resetMixpanelData = (key: string) => {
    setMixpanelDataByKeyHandler({
      key,
      payload: MIXPANEL_DEFAULT_STATE[key],
    });
  };

  const trackCreditsPurchased = async (additionalPayload = {}) => {
    if (!MIXPANEL_ENABLE || !isAccountIsMixpanelEligableEnabled) {
      resetMixpanelData(EVENT_NAMES.CREDITS_PURCHASED);
      removeLocalItem(MIXPANEL_CREDITS_PURCHASED);
      return;
    }

    try {
      const localStorageData = JSON.parse(getLocalItem(MIXPANEL_CREDITS_PURCHASED));

      const shouldUseLocalStorageData = localStorageData !== null && typeof localStorageData === 'object';
      const data = shouldUseLocalStorageData ? localStorageData : mixpanelData[EVENT_NAMES.CREDITS_PURCHASED];

      if (data.Amount > 0) {
        const payload = {
          event_name: EVENT_NAMES.CREDITS_PURCHASED,
          source: data.Source ? data.Source : 'Direct URL',
          amount: data.Amount,
          total_costs: data.Total,
          auto_topup: data.AutoTopup,
          payment_method: data.PaymentMethod ?? null,
          melissa_used: false,
          payment_saved: data.PaymentSaved,
          ...additionalPayload,
        };

        await sdk.mixpanelTrack(payload);
      }

      resetMixpanelData(EVENT_NAMES.CREDITS_PURCHASED);
      removeLocalItem(MIXPANEL_CREDITS_PURCHASED);
    } catch (error) {
      Sentry.captureException(error, { tags: { component: 'useMixPanel', action: 'trackCreditsPurchased' } });
    }
  };

  const trackPackagesPageVisited = payload => {
    if (!accountHashId && !isAccountIsMixpanelEligableEnabled) return;

    // add event property when user try to unclock offer without any credits left
    if (payload['Source'] === 'Unlock Attempt') {
      setMixpanelDataByKeyHandler({
        key: EVENT_NAMES.CONVERSATION_UNLOCKED,
        payload: { triggerToBuy: true },
      });
    }

    setMixpanelDataByKeyHandler({
      key: EVENT_NAMES.CREDITS_PURCHASED,
      payload,
    });

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PACKAGES_VISITED,
      payload: {
        eventProperties: payload,
      },
    });
  };

  const trackProfileView = (payload: TrackProfileViewProps) => {
    if (payload.Source !== '' && payload.User !== '') {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.PROFILE_VIEW,
        payload: {
          eventProperties: payload,
          incrementalProperties: { '# profile views': 1 },
        },
        callBack: () => trackPageView('view_member_profile'),
      });
    }
  };

  const trackPageView = (pathname: string) => {
    const pageName = TRACK_PAGE_VIEW_PATHS[pathname];

    if (currentRoute === pathname) {
      return;
    }

    setCurrentRoute(pathname);

    if (pageName) {
      const overidePageName = localStorage.getItem('mxTrackPageView_page_name');
      const pageNameValue = overidePageName ? overidePageName : pageName;

      mixpanelTrackEvent({
        eventName: EVENT_NAMES.PAGE_VIEW,
        payload: {
          eventProperties: {
            'Page Name': pageNameValue,
          },
        },
        callBack: () => {
          localStorage.removeItem('mxTrackPageView_page_name');
        },
      });
    }
  };

  const trackPageViewAnonymously = (pathname: string) => {
    const pageName = TRACK_PAGE_VIEW_PATHS[pathname];

    if (currentRoute === pathname) {
      return;
    }

    setCurrentRoute(pathname);

    if (pageName) {
      mixpanelTrackEventAnonymously({
        eventName: EVENT_NAMES.PAGE_VIEW,
        payload: {
          eventProperties: {
            'Page Name': pageName,
          },
        },
      });
    }
  };

  return {
    trackCreditsPurchased,
    trackPackagesPageVisited,
    trackProfileView,
    trackPageView,
    mixpanelData,
    setMixpanelDataByKeyHandler,
    resetMixpanelData,
    trackPageViewAnonymously,
  };
};

export default useMixPanel;
